import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Target, Users, DollarSign, Phone, MessageSquare, 
  BarChart3, Settings, Brain, Zap, ChevronDown, ChevronUp,
  CheckCircle, AlertTriangle, TrendingUp, BookOpen, Lock
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Theme
const theme = {
  bg: '#f5f3f0',
  card: '#ffffff',
  border: '#e0dbd4',
  text: '#1a1a1a',
  textMuted: 'rgba(26,26,26,0.6)',
  orange: '#F05A1A',
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
  purple: '#7c3aed',
  blue: '#1A4A8A',
};

// Access code
const ONBOARDING_CODE = 'closer10';

// Lock Screen
const LockScreen = ({ onUnlock }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.toLowerCase() === ONBOARDING_CODE) {
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: theme.bg }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full text-center"
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(124,58,237,0.1)' }}>
          <Lock className="w-8 h-8" style={{ color: theme.purple }} />
        </div>
        <h1 className="font-bebas text-3xl text-gray-800 mb-2">ONBOARDING PRSNL</h1>
        <p className="text-gray-500 mb-6 text-sm">Accès réservé aux Setters & Closers</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code d'accès"
            className={`w-full px-4 py-3 rounded-xl border-2 text-center font-bold tracking-widest uppercase outline-none transition-colors ${
              error ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-purple-500'
            }`}
          />
          {error && <p className="text-red-500 text-sm mt-2">Code incorrect</p>}
          <button
            type="submit"
            className="w-full mt-4 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.purple }}
          >
            ACCÉDER À L'ONBOARDING
          </button>
        </form>
        
        <Link 
          to="/recrutement?tab=closer" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm mt-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au recrutement
        </Link>
      </motion.div>
    </div>
  );
};

// Expandable Section
const Section = ({ title, icon, children, defaultOpen = false, number }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: theme.purple }}>
            {number}
          </span>
          <span className="text-lg">{icon}</span>
          <span className="font-bold text-gray-800">{title}</span>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
          <ChevronDown className="w-5 h-5 text-purple-600" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Info Box
const InfoBox = ({ type = 'info', children }) => {
  const styles = {
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
    success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
    danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
  };
  const s = styles[type];
  
  return (
    <div className={`${s.bg} ${s.border} border rounded-lg p-4 ${s.text}`}>
      {children}
    </div>
  );
};

// Quote Block
const Quote = ({ children }) => (
  <div className="border-l-4 border-purple-400 pl-4 py-2 my-4 italic text-gray-600 bg-purple-50 rounded-r-lg pr-4">
    {children}
  </div>
);

// List Item
const ListItem = ({ children, icon = '👉' }) => (
  <div className="flex items-start gap-2 py-1">
    <span className="flex-shrink-0">{icon}</span>
    <span className="text-gray-700">{children}</span>
  </div>
);

const OnboardingPage = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);

  if (!isUnlocked) {
    return <LockScreen onUnlock={() => setIsUnlocked(true)} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/recrutement?tab=closer" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la page Closer
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-white" />
            <h1 className="text-white font-bebas text-4xl tracking-wide">
              ONBOARDING PRSNL
            </h1>
          </div>
          <p className="text-white/80 text-sm">
            Formation complète Setter & Closer · Version 1.0
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        
        {/* Objectif */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 border-2 border-purple-300 mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-6 h-6 text-purple-600" />
            <h2 className="font-bebas text-2xl text-purple-600">OBJECTIF</h2>
          </div>
          <p className="text-gray-700 mb-4">
            Former un commercial capable de :
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-500" />
              <span>Comprendre un restaurateur en 5 minutes</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-500" />
              <span>Qualifier rapidement</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-500" />
              <span>Structurer un call</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-500" />
              <span>Closer de manière logique et chiffrée</span>
            </div>
          </div>
          <InfoBox type="warning">
            <p className="font-semibold">👉 Ici on ne vend pas du rêve</p>
            <p>👉 On vend de la <strong>rentabilité</strong> et du <strong>système</strong></p>
          </InfoBox>
        </motion.div>

        {/* Module 1 */}
        <Section title="COMPRENDRE LE BUSINESS" icon="🧠" number="1" defaultOpen>
          <h3 className="font-bold text-gray-800 mb-3">🏢 PRSNL = quoi ?</h3>
          <p className="text-gray-600 mb-4">PRSNL aide les restaurateurs à :</p>
          <div className="space-y-1 mb-4">
            <ListItem>augmenter leur rentabilité</ListItem>
            <ListItem>structurer leur business</ListItem>
            <ListItem>sortir de l'opérationnel</ListItem>
            <ListItem>mettre en place des systèmes</ListItem>
          </div>
          
          <InfoBox type="info">
            <p className="font-semibold mb-2">👉 Notre client :</p>
            <ul className="space-y-1 text-sm">
              <li>• fait déjà du CA</li>
              <li>• mais ne gagne pas assez</li>
              <li>• ou travaille trop</li>
            </ul>
          </InfoBox>
          
          <div className="mt-6 bg-purple-100 rounded-xl p-5 border border-purple-200">
            <h4 className="font-bold text-purple-800 mb-2">💰 NOTRE PROMESSE</h4>
            <Quote>
              "On t'aide à gagner plus d'argent et reprendre le contrôle de ton resto."
            </Quote>
          </div>
        </Section>

        {/* Module 2 */}
        <Section title="COMPRENDRE LE CLIENT" icon="🎯" number="2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-800 mb-2">👤 PROFIL</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Restaurateur (1 ou plusieurs établissements)</li>
                <li>• Débordé</li>
                <li>• Souvent seul</li>
                <li>• Peu structuré</li>
              </ul>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h4 className="font-bold text-red-800 mb-2">🔥 PROBLÈMES PRINCIPAUX</h4>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Food cost trop élevé</li>
                <li>• Masse salariale mal maîtrisée</li>
                <li>• Peu ou pas de process</li>
                <li>• Dépendance au patron</li>
                <li>• Pas de vision financière</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 mb-4">
            <h4 className="font-bold text-amber-800 mb-2">💭 CE QU'IL PENSE</h4>
            <ul className="text-sm text-amber-700 space-y-1 italic">
              <li>"Je bosse beaucoup mais je gagne peu"</li>
              <li>"J'ai pas le temps"</li>
              <li>"C'est normal dans la restauration"</li>
            </ul>
          </div>
          
          <InfoBox type="success">
            <p className="font-semibold">💡 OBJECTIF DU SETTER / CLOSER</p>
            <p className="mt-1">👉 Lui faire réaliser : <strong>"Mon problème est structurable et solvable."</strong></p>
          </InfoBox>
        </Section>

        {/* Module 3 */}
        <Section title="COMPRENDRE LE DIAGNOSTIC" icon="📥" number="3">
          <p className="text-gray-600 mb-4">Chaque lead vient d'un audit.</p>
          
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 mb-4">
            <h4 className="font-bold text-purple-800 mb-2">👉 Tu dois analyser :</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-white rounded p-2 border">Score global</div>
              <div className="bg-white rounded p-2 border">Food cost</div>
              <div className="bg-white rounded p-2 border">Masse salariale</div>
              <div className="bg-white rounded p-2 border">Marge nette</div>
            </div>
          </div>
          
          <InfoBox type="danger">
            <h4 className="font-semibold mb-2">💣 EXEMPLE</h4>
            <ul className="text-sm space-y-1">
              <li>• Food cost à 40% = <strong>problème</strong></li>
              <li>• Masse salariale à 35%+ = <strong>problème</strong></li>
              <li>• Marge faible = <strong>énorme levier</strong></li>
            </ul>
            <p className="mt-2 font-semibold">👉 C'est TON angle de vente</p>
          </InfoBox>
        </Section>

        {/* Module 4 */}
        <Section title="SETTER (PRISE DE RDV)" icon="📲" number="4">
          <div className="bg-amber-100 rounded-lg p-4 border border-amber-300 mb-4">
            <h4 className="font-bold text-amber-800">🎯 OBJECTIF</h4>
            <p className="text-amber-700">👉 Transformer un lead en RDV qualifié</p>
          </div>
          
          <h4 className="font-bold text-gray-800 mb-3">🧠 STRUCTURE</h4>
          
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
              <p className="font-semibold text-gray-800">1. Accroche</p>
              <p className="text-gray-600 italic mt-1">"Salut [prénom], j'ai vu ton audit, intéressant 👀"</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
              <p className="font-semibold text-gray-800">2. Compréhension</p>
              <p className="text-gray-600 italic mt-1">"C'est quoi ton plus gros problème aujourd'hui ?"</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
              <p className="font-semibold text-gray-800">3. Qualification</p>
              <p className="text-gray-600 mt-1">Questions clés :</p>
              <ul className="text-sm text-gray-600 mt-1 space-y-1">
                <li>• Tu fais combien de CA ?</li>
                <li>• Tu travailles combien d'heures ?</li>
                <li>• T'as un manager ?</li>
                <li>• Tu gagnes combien à la fin ?</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
              <p className="font-semibold text-gray-800">4. Projection</p>
              <p className="text-gray-600 italic mt-1">"Si tu pouvais améliorer ça, ça changerait quoi pour toi ?"</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-amber-400">
              <p className="font-semibold text-gray-800">5. RDV</p>
              <p className="text-gray-600 italic mt-1">"Je pense qu'on peut vraiment t'aider, je te propose un échange rapide avec mon expert."</p>
            </div>
          </div>
          
          <InfoBox type="warning">
            <p className="font-semibold">⚠️ RÈGLE</p>
            <p>👉 Le setter ne vend PAS</p>
            <p>👉 Il qualifie et crée l'intérêt</p>
          </InfoBox>
        </Section>

        {/* Module 5 */}
        <Section title="CLOSER" icon="📞" number="5">
          <div className="bg-blue-100 rounded-lg p-4 border border-blue-300 mb-4">
            <h4 className="font-bold text-blue-800">🎯 OBJECTIF</h4>
            <p className="text-blue-700">👉 Faire prendre une décision logique</p>
          </div>
          
          <h4 className="font-bold text-gray-800 mb-3">🧠 STRUCTURE D'UN CALL</h4>
          
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
              <p className="font-semibold text-gray-800">1. Intro</p>
              <p className="text-gray-600 text-sm mt-1">• Créer confiance • Cadrer le call</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
              <p className="font-semibold text-gray-800">2. Analyse situation</p>
              <p className="text-gray-600 italic mt-1">"Parle-moi de ton resto aujourd'hui"</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
              <p className="font-semibold text-gray-800">3. Problèmes</p>
              <p className="text-gray-600 italic mt-1">"C'est quoi ce qui t'empêche de gagner plus ?"</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
              <p className="font-semibold text-gray-800">4. GAP</p>
              <p className="text-gray-600 text-sm mt-1">👉 Montrer l'écart entre : situation actuelle et objectif</p>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-400">
              <p className="font-semibold text-red-800">5. CHIFFRAGE (TRÈS IMPORTANT)</p>
              <p className="text-red-700 mt-1">👉 Ex : "3% de food cost = X€ / an"</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
              <p className="font-semibold text-gray-800">6. Solution</p>
              <p className="text-gray-600 text-sm mt-1">👉 Présenter PRSNL</p>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-400">
              <p className="font-semibold text-green-800">7. Closing</p>
              <p className="text-green-700 italic mt-1">"Est-ce que tu veux continuer comme ça ou changer ?"</p>
            </div>
          </div>
        </Section>

        {/* Module 6 */}
        <Section title="OBJECTIONS" icon="💣" number="6">
          <div className="space-y-4">
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="font-bold text-red-700">❌ "J'ai pas le temps"</p>
              <p className="text-gray-700 mt-2 italic">"Justement, c'est parce que t'as pas de système."</p>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="font-bold text-red-700">❌ "J'ai pas le budget"</p>
              <p className="text-gray-700 mt-2 italic">"Combien tu perds chaque mois aujourd'hui ?"</p>
            </div>
            
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="font-bold text-red-700">❌ "Je réfléchis"</p>
              <p className="text-gray-700 mt-2 italic">"Qu'est-ce qui te bloque concrètement ?"</p>
            </div>
          </div>
        </Section>

        {/* Module 7 */}
        <Section title="KPI" icon="📊" number="7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <h4 className="font-bold text-amber-800 mb-3">SETTER</h4>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  % leads appelés
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  % RDV
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  % joignable
                </li>
              </ul>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-bold text-blue-800 mb-3">CLOSER</h4>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  % closing
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  CA généré
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  no-show
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Module 8 */}
        <Section title="UTILISATION DU CRM" icon="⚙️" number="8">
          <p className="text-gray-600 mb-4">👉 Chaque lead = une fiche</p>
          
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 mb-4">
            <h4 className="font-bold text-purple-800 mb-2">OBLIGATOIRE :</h4>
            <ul className="text-sm space-y-1 text-purple-700">
              <li>• Remplir notes</li>
              <li>• Statut à jour</li>
              <li>• Infos setter + closer</li>
            </ul>
          </div>
          
          <InfoBox type="danger">
            <p className="font-semibold">👉 Sinon = perte d'argent</p>
          </InfoBox>
          
          <div className="mt-4">
            <Link
              to="/crm"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
              style={{ backgroundColor: theme.orange }}
            >
              <BarChart3 className="w-5 h-5" />
              Accéder au CRM
            </Link>
            <p className="text-xs text-gray-500 mt-2">Demande le code à Sabril si besoin</p>
          </div>
        </Section>

        {/* Principes clés */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 text-white mb-6">
          <h3 className="font-bebas text-2xl mb-4">🧠 PRINCIPES CLÉS</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm">Tu poses plus de questions que tu ne parles</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm">Tu comprends avant de vendre</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm">Tu simplifies toujours</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm">Tu chiffres toujours</span>
            </div>
          </div>
        </div>

        {/* Objectif Final */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 text-white mb-6">
          <h3 className="font-bebas text-2xl mb-4">🚀 OBJECTIF FINAL</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-bold text-amber-300 mb-2">👉 Le setter :</p>
              <ul className="text-sm space-y-1">
                <li>• Qualifie rapidement</li>
                <li>• Book des RDV propres</li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-bold text-blue-300 mb-2">👉 Le closer :</p>
              <ul className="text-sm space-y-1">
                <li>• Comprend vite</li>
                <li>• Structure son call</li>
                <li>• Close avec logique</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bonus */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white">
          <h3 className="font-bebas text-2xl mb-4">🔥 BONUS</h3>
          <Quote>
            <p className="text-white not-italic font-medium">
              "Si tu comprends pas ton client, tu vendras jamais.
            </p>
            <p className="text-white not-italic font-medium mt-2">
              Si tu ne poses pas de questions, tu perdras toujours."
            </p>
          </Quote>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 pb-12">
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link 
              to="/closer-call-script"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-colors"
              style={{ backgroundColor: theme.purple }}
            >
              <Phone className="w-4 h-4" />
              Script Visio Closer
            </Link>
            <Link 
              to="/crm"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-colors"
              style={{ backgroundColor: theme.orange }}
            >
              <BarChart3 className="w-4 h-4" />
              Accéder au CRM
            </Link>
          </div>
          <p className="text-gray-400 text-xs mt-6">
            © 2025 PRSNL · Onboarding v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
